import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, CheckCircle2, Gift, Lock } from 'lucide-react';
import { Footer } from '../components/Footer';
import { useHotmartSalesFunnel } from '../hooks/useHotmartSalesFunnel';
const MOUNT_ID = 'hotmart-sf-oportunidad1';
function CountdownBlock() {
  const [timeLeft, setTimeLeft] = useState(5 * 60); // 5 minutes in seconds
  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  return <div className="text-center mb-8 mt-4" data-id="element-750">
      <p className="text-sm font-medium text-red-600 mb-2" data-id="element-751">
        Esta oferta dejará de existir en:
      </p>
      <div className="inline-flex items-center gap-2 bg-red-50 border-2 border-red-200 rounded-xl px-6 py-3" data-id="element-752">
        <span className="text-3xl md:text-4xl font-black text-red-600 tabular-nums tracking-wider" data-id="element-753">
          {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
        </span>
      </div>
    </div>;
}
export function Oportunidad1Page() {
  useHotmartSalesFunnel(MOUNT_ID);
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.6
    }
  };
  const scrollToHotmart = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById('oferta-mesa-en-paz');
    if (el) {
      const yOffset = -100;
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-white font-sans selection:bg-[#FFF3E0] selection:text-[#1B3A5C] flex flex-col" data-id="element-754">
      {/* 1. URGENCY BANNER (Static) */}
      <div className="bg-[#FFF8F0] text-[#D97706] py-2 px-4 text-center text-sm font-medium border-b border-[#FFE0B2]" data-id="element-755">
        ⌛ 8 de cada 10 mamás completaron su pedido.
      </div>

      <main className="max-w-3xl mx-auto px-4 py-12 md:py-16 flex-grow w-full" data-id="element-756">
        {/* 2. SECTION 1 — HEADLINE DE IMPACTO */}
        <motion.section {...fadeIn} className="text-center mb-12" data-id="element-757">
          <h2 className="text-2xl md:text-3xl font-bold text-[#F4A261] mb-4" data-id="element-758">
            ¡Felicidades por NutriPeques! 🎉
          </h2>
          <p className="text-lg text-gray-500 mb-6 font-medium" data-id="element-759">
            Pero antes de ir al contenido... una pregunta honesta:
          </p>
          <h1 className="text-3xl md:text-5xl font-black text-[#1B3A5C] leading-tight max-w-2xl mx-auto" data-id="element-760">
            ¿Qué vas a hacer si cocinás con todo tu amor y tu hijo te dice "NO
            QUIERO"? 🚩
          </h1>
        </motion.section>

        {/* 3. SECTION 2 — EL PUENTE LÓGICO */}
        <motion.section {...fadeIn} className="mb-16 max-w-2xl mx-auto text-center" data-id="element-761">
          <div className="text-lg md:text-xl text-gray-700 space-y-6" data-id="element-762">
            <p data-id="element-763">
              Tenés las recetas. Tenés la nutrición. Pero si tu hijo tiene un{' '}
              <strong data-id="element-764">bloqueo alimentario</strong>, las recetas solas no abren
              su boca.
            </p>
            <p data-id="element-765">No es capricho. Es biología.</p>
            <p data-id="element-766">
              Y si intentás resolverlo con sobornos{' '}
              <em data-id="element-767">("comé y te doy postre")</em>, solo lo alejás más de la comida
              real.
            </p>
          </div>
        </motion.section>

        {/* 4. SECTION 3 — LA SOLUCIÓN */}
        <motion.section {...fadeIn} className="text-center mb-16" data-id="element-768">
          <h2 className="text-2xl md:text-3xl font-black text-[#1B3A5C] mb-2" data-id="element-769">
            Presentamos: LA MESA EN PAZ 🧠
          </h2>
          <h3 className="text-lg md:text-xl text-[#F4A261] font-bold mb-10" data-id="element-770">
            El Sistema de Psicología Alimentaria para Niños que Dicen que "NO"
          </h3>

          <img src="https://cdn.magicpatterns.com/uploads/3zShgG6ZBFHJeoMBasq6tM/mesaenpaz.jpg" alt="La Mesa en Paz" className="w-full max-w-md mx-auto rounded-xl shadow-2xl mb-10 transform hover:scale-[1.02] transition-transform duration-500" data-id="element-771" />

          <p className="text-lg text-gray-700 max-w-2xl mx-auto leading-relaxed" data-id="element-772">
            Este no es otro libro de cocina. Es el manual de instrucciones para
            el cerebro de tu hijo. Es el sistema para transformar la hora de
            comer de un campo de batalla a un momento de conexión en solo{' '}
            <strong data-id="element-773">30 días</strong>.
          </p>
        </motion.section>

        {/* 5. SECTION 3.1 & 3.2 — VALOR Y OFERTA + HOTMART WIDGET */}
        <motion.section id="oferta-mesa-en-paz" {...fadeIn} className="bg-[#FFF8F0] rounded-[2rem] p-8 md:p-12 shadow-lg border border-[#FFE0B2] mb-16" data-id="element-774">
          <div className="text-center mb-10" data-id="element-775">
            <p className="text-gray-700 mb-6 text-lg" data-id="element-776">
              Este sistema tiene un valor de <strong data-id="element-777">$200 USD</strong> porque
              reemplaza meses de terapia nutricional.
            </p>
            <p className="font-bold text-[#F4A261] text-xl mb-4" data-id="element-778">
              Hoy, solo por ser cliente de NutriPeques:
            </p>
            <h3 className="text-5xl md:text-6xl font-black text-[#1B3A5C] mb-6" data-id="element-779">
              Solo $27 USD
            </h3>
            <p className="text-gray-500 italic text-sm md:text-base" data-id="element-780">
              (Esta oferta desaparece cuando cerrás esta página. No está
              disponible en nuestra web ni en redes sociales).
            </p>
          </div>

          {/* HOTMART WIDGET CONTAINER */}
          <div id={MOUNT_ID} className="min-h-[100px] flex items-center justify-center mb-8 relative z-10" data-id="element-781">
            {/* Widget mounts here */}
          </div>

          {/* 5-MINUTE COUNTDOWN TIMER */}
          <CountdownBlock data-id="element-782" />

          <div className="flex items-start gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-783">
            <Lock className="w-8 h-8 text-[#1B3A5C] shrink-0 mt-1" data-id="element-784" />
            <p className="text-sm text-gray-600 leading-relaxed" data-id="element-785">
              <strong data-id="element-786">Garantía total de 30 días.</strong> Si no sentís que la
              paz vuelve a tu mesa, te devolvemos cada centavo. Sin preguntas.
            </p>
          </div>
        </motion.section>

        {/* 7. SECTION 4 — QUÉ VA A LOGRAR */}
        <motion.section {...fadeIn} className="mb-16" data-id="element-787">
          <h3 className="text-2xl md:text-3xl font-black text-[#1B3A5C] text-center mb-8" data-id="element-788">
            Lo que vas a lograr:
          </h3>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8" data-id="element-789">
            <ul className="space-y-6" data-id="element-790">
              <li className="flex items-start gap-4" data-id="element-791">
                <CheckCircle2 className="w-6 h-6 text-[#F4A261] shrink-0 mt-0.5" data-id="element-792" />
                <p className="text-gray-700 text-lg" data-id="element-793">
                  <strong className="text-[#1B3A5C]" data-id="element-794">
                    Protocolo de Exposición Progresiva:
                  </strong>{' '}
                  Cómo presentar comida nueva sin que él se sienta amenazado.
                </p>
              </li>
              <li className="flex items-start gap-4" data-id="element-795">
                <CheckCircle2 className="w-6 h-6 text-[#F4A261] shrink-0 mt-0.5" data-id="element-796" />
                <p className="text-gray-700 text-lg" data-id="element-797">
                  <strong className="text-[#1B3A5C]" data-id="element-798">Scripts de Crisis:</strong>{' '}
                  Qué decir exactamente (palabra por palabra) cuando escupe la
                  comida o hace un berrinche.
                </p>
              </li>
              <li className="flex items-start gap-4" data-id="element-799">
                <CheckCircle2 className="w-6 h-6 text-[#F4A261] shrink-0 mt-0.5" data-id="element-800" />
                <p className="text-gray-700 text-lg" data-id="element-801">
                  <strong className="text-[#1B3A5C]" data-id="element-802">
                    Ingeniería del Plato:
                  </strong>{' '}
                  El truco visual de los "3 colores" que desactiva el rechazo
                  neurológico al instante.
                </p>
              </li>
              <li className="flex items-start gap-4" data-id="element-803">
                <CheckCircle2 className="w-6 h-6 text-[#F4A261] shrink-0 mt-0.5" data-id="element-804" />
                <p className="text-gray-700 text-lg" data-id="element-805">
                  <strong className="text-[#1B3A5C]" data-id="element-806">Cero Peleas:</strong> Cómo
                  aplicar la "División de Responsabilidades" para que vos
                  decidas qué hay y él decida cuánto come, sin llantos.
                </p>
              </li>
            </ul>
          </div>
        </motion.section>

        {/* 8. SECTION 5 — BONO EXCLUSIVO */}
        <motion.section {...fadeIn} className="mb-16" data-id="element-807">
          <div className="bg-gradient-to-br from-[#FFF8F0] to-white rounded-2xl p-8 shadow-sm border border-[#FFE0B2] flex flex-col md:flex-row items-center md:items-start gap-6" data-id="element-808">
            <div className="bg-[#F4A261] p-4 rounded-full text-white shrink-0" data-id="element-809">
              <Gift className="w-8 h-8" data-id="element-810" />
            </div>
            <div className="text-center md:text-left" data-id="element-811">
              <h4 className="text-xl font-black text-[#1B3A5C] mb-3" data-id="element-812">
                REGALO EXTRA
              </h4>
              <p className="text-gray-700 leading-relaxed" data-id="element-813">
                Si sumás el sistema ahora, incluimos el{' '}
                <strong data-id="element-814">SCRIPT PASO A PASO DE LOS PRIMEROS 7 DÍAS</strong>. Una
                guía diaria de qué decirle y qué hacer desde el primer minuto
                para resetear su paladar.
              </p>
            </div>
          </div>
        </motion.section>

        {/* 9. SECTION 6 — VALOR Y OFERTA (Second Price Anchor) */}
        <motion.section {...fadeIn} className="bg-[#FFF8F0] rounded-3xl p-8 md:p-12 text-center shadow-sm border border-[#FFE0B2] mb-12" data-id="element-815">
          <p className="text-gray-700 mb-4 text-lg" data-id="element-816">
            Este sistema tiene un valor de{' '}
            <span className="line-through decoration-red-400 decoration-2" data-id="element-817">
              $200 USD
            </span>{' '}
            porque reemplaza meses de terapia nutricional.
          </p>
          <p className="font-bold text-[#F4A261] text-xl mb-6" data-id="element-818">
            Hoy, solo por ser cliente de NutriPeques:
          </p>
          <h3 className="text-5xl md:text-6xl font-black text-[#1B3A5C] mb-6" data-id="element-819">
            Solo $27 USD
          </h3>
          <p className="text-gray-500 italic text-sm md:text-base" data-id="element-820">
            (Esta oferta desaparece cuando cerrás esta página. No está
            disponible en nuestra web ni en redes sociales).
          </p>
        </motion.section>

        {/* 10. SECTION 7 — SECOND CTA BUTTON */}
        <motion.section {...fadeIn} className="text-center mb-16" data-id="element-821">
          <button onClick={scrollToHotmart} className="w-full md:w-auto inline-block bg-[#F4A261] hover:bg-[#E76F51] text-white font-black text-xl md:text-2xl px-8 py-5 rounded-2xl shadow-[0_8px_0_#D97706] hover:shadow-[0_4px_0_#D97706] hover:translate-y-1 transition-all" data-id="element-822">
            SÍ, QUIERO LA MESA EN PAZ POR $27 USD
          </button>
          <p className="text-gray-500 font-medium mt-6" data-id="element-823">
            Acceso inmediato. Un solo pago. Sin suscripciones.
          </p>
        </motion.section>

        {/* 11. SECTION 8 — CIERRE DE SEGURIDAD */}
        <motion.section {...fadeIn} className="text-center max-w-xl mx-auto" data-id="element-824">
          <div className="flex flex-col items-center justify-center gap-4 mb-8" data-id="element-825">
            <Lock className="w-12 h-12 text-[#1B3A5C]" data-id="element-826" />
            <p className="text-sm text-gray-600" data-id="element-827">
              <strong data-id="element-828">Garantía total de 30 días.</strong> Si no sentís que la
              paz vuelve a tu mesa, te devolvemos cada centavo. Sin preguntas.
            </p>
          </div>

          <div className="space-y-3" data-id="element-829">
            <a href="/thankyou1" className="block text-gray-500 hover:text-gray-800 underline transition-colors text-sm md:text-base" data-id="element-830">
              ¿No querés sumar La Mesa en Paz hoy? → Continuar sin este sistema.
            </a>
            <p className="text-xs text-gray-400" data-id="element-831">
              (Esta oferta no estará disponible después de esta página.)
            </p>
          </div>
        </motion.section>
      </main>

      <Footer data-id="element-832" />
    </div>;
}